import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useShortcuts } from '../lib/keys';
import { ago, phone } from '../lib/format';
import { markSosTaken, useActiveSos, useDashboard } from '../lib/queries';
import { useRealtime, useRealtimeEvent } from '../lib/realtime';
import { qk } from '../lib/realtime-cache';
import { playSosChime } from '../lib/sosSound';
import type { SosRecord } from '../lib/types';
import {
  IconAudit,
  IconCash,
  IconDrivers,
  IconFares,
  IconGift,
  IconLive,
  IconMore,
  IconPercent,
  IconRiders,
  IconRides,
  IconSettings,
  IconSignOut,
  IconSos,
  IconStaff,
  IconStar,
  Logo,
} from './icons';
import { Avatar, Dialog, ThemeSwitch, useDialogClose } from './ui';

type NavItem = { to: string; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; exact?: boolean; count?: number; sos?: boolean };

function Brand() {
  return (
    <Link to="/" className="brand" aria-label="Zoomo Rides admin, home">
      <Logo size={36} />
      <div>
        <div className="brand-name">Zoomo Rides</div>
        <div className="brand-sub">Admin</div>
      </div>
    </Link>
  );
}

export function ConnectionStatus() {
  const { connected } = useRealtime();
  return (
    <span className="conn" title={connected ? 'Live updates are on' : 'Reconnecting — refreshing every 5 seconds'}>
      <span className={`dot ${connected ? 'on live' : 'off'}`} />
      <span className="conn-label">{connected ? 'Live' : 'Reconnecting'}</span>
    </span>
  );
}

function useNav() {
  const { data: dash } = useDashboard();
  const sosOpen = dash?.sosOpen ?? 0;
  const groups: { title: string; items: NavItem[] }[] = [
    {
      title: 'Operate',
      items: [
        { to: '/', label: 'Live', icon: IconLive, exact: true },
        { to: '/sos', label: 'SOS', icon: IconSos, count: sosOpen, sos: true },
        { to: '/rides', label: 'Rides', icon: IconRides },
        { to: '/drivers', label: 'Drivers', icon: IconDrivers, count: dash?.drivers.byStatus.PENDING },
        { to: '/riders', label: 'Riders', icon: IconRiders },
      ],
    },
    {
      title: 'Money',
      items: [
        { to: '/payouts', label: 'Payouts', icon: IconCash },
        { to: '/fares', label: 'Fares', icon: IconFares },
        { to: '/commission', label: 'Commission', icon: IconPercent },
        { to: '/rewards', label: 'Rewards', icon: IconGift },
      ],
    },
    { title: 'Quality', items: [{ to: '/ratings', label: 'Ratings', icon: IconStar }] },
    {
      title: 'Team',
      items: [
        { to: '/settings', label: 'Settings', icon: IconSettings },
        { to: '/staff', label: 'Staff', icon: IconStaff },
        { to: '/audit', label: 'Audit log', icon: IconAudit },
      ],
    },
  ];
  return { groups, sosOpen };
}

const isActive = (path: string, n: NavItem) => (n.exact ? path === n.to : path === n.to || path.startsWith(`${n.to}/`));

function Count({ n }: { n: NavItem }) {
  if (!n.count) return null;
  return <span className={`nav-count${n.sos ? ' bad' : ''}`}>{n.count}</span>;
}

/** Red strip on every page while an SOS is open; quieter once someone has taken it. */
function SosStrip({ items }: { items: SosRecord[] }) {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ack = useMutation({
    mutationFn: (id: string) => api.post<SosRecord>(`/admin/sos/${id}/acknowledge`),
    onMutate: (id) => markSosTaken(qc, id, admin?.id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.sos });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
    onError: (e) => {
      setError(errorMessage(e));
      void qc.invalidateQueries({ queryKey: qk.sos });
    },
  });
  const open = items.filter((s) => s.status === 'OPEN');
  const first = open[0] ?? items[0];
  if (!first) return null;
  const who = first.raisedByType === 'RIDER' ? first.rideSnapshot?.rider : first.rideSnapshot?.driver;
  const handled = open.length === 0;
  return (
    <div className={`sos-strip${handled ? ' handled' : ''}`} role="alert" aria-label="SOS alert" data-testid="sos-strip">
      <span className="beacon" aria-hidden="true" />
      <Link to="/sos" className="sos-text" aria-label="Open SOS page">
        <strong>
          {handled
            ? `${items.length === 1 ? 'An SOS is' : `${items.length} SOS alerts are`} being handled`
            : open.length === 1
              ? 'SOS — someone needs help now'
              : `${open.length} SOS alerts need someone now`}
        </strong>
        <div className="sos-sub truncate">
          {first.raisedByType === 'RIDER' ? 'Rider' : 'Driver'} {who?.name ?? 'unknown'} · {phone(who?.phone)} · {ago(first.createdAt)}
          {first.note ? ` · “${first.note}”` : ''}
        </div>
        {error && <div className="sos-sub">{error}</div>}
      </Link>
      <div className="sos-actions row" style={{ gap: 8, flexWrap: 'nowrap' }}>
        {!handled && (
          <button className="btn btn-on-red" disabled={ack.isPending} onClick={() => ack.mutate(first.id)}>
            I’m on it
          </button>
        )}
        <Link to="/sos" className={`btn hide-phone ${handled ? '' : 'btn-ghost-red'}`}>
          Open SOS
        </Link>
      </div>
    </div>
  );
}

function MoreSheet({ items, path, onClose }: { items: NavItem[]; path: string; onClose: () => void }) {
  return (
    <Dialog title="Menu" onClose={onClose}>
      <MoreBody items={items} path={path} />
    </Dialog>
  );
}
function MoreBody({ items, path }: { items: NavItem[]; path: string }) {
  const close = useDialogClose();
  const { admin, logout } = useAuth();
  return (
    <div className="stack-sm" style={{ gap: 16 }}>
      <nav className="more-grid" aria-label="More pages">
        {items.map((n) => (
          <Link key={n.to} to={n.to} className={`more-item${isActive(path, n) ? ' active' : ''}`} onClick={close}>
            <n.icon />
            {n.label}
            <Count n={n} />
          </Link>
        ))}
      </nav>
      <div className="card" style={{ boxShadow: 'none' }}>
        <Link to="/account" className="row-link" onClick={close}>
          <Avatar name={admin?.name} />
          <span className="row-main">
            <span className="row-title">{admin?.name}</span>
            <span className="row-sub">{admin?.role === 'OWNER' ? 'Owner' : 'Admin'} · your account</span>
          </span>
        </Link>
      </div>
      <div className="spread">
        <ThemeSwitch />
        <button className="btn btn-ghost" onClick={() => void logout()}>
          <IconSignOut /> Sign out
        </button>
      </div>
    </div>
  );
}

export function Shell() {
  const { admin } = useAuth();
  const { groups, sosOpen } = useNav();
  const sos = useActiveSos();
  const [more, setMore] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    document.title = sosOpen ? `(${sosOpen}) SOS · Zoomo Rides Admin` : 'Zoomo Rides Admin';
  }, [sosOpen]);
  useRealtimeEvent(['sos:new'], () => playSosChime());
  useShortcuts();

  const all = groups.flatMap((g) => g.items);
  const tabs = all.filter((n) => ['/', '/sos', '/rides', '/drivers'].includes(n.to));
  const rest = all.filter((n) => !tabs.includes(n));
  const moreActive = rest.some((n) => isActive(path, n)) || path === '/account';

  const link = (n: NavItem) => (
    <Link
      key={n.to}
      to={n.to}
      className={`nav-link${n.sos ? ' sos-link' : ''}`}
      activeProps={{ className: `nav-link active${n.sos ? ' sos-link' : ''}`, 'aria-current': 'page' }}
      activeOptions={{ exact: !!n.exact }}
      title={n.label}
    >
      <n.icon />
      <span>{n.label}</span>
      <Count n={n} />
    </Link>
  );

  return (
    <div className="shell">
      <header className="topbar">
        <Brand />
        <ConnectionStatus />
        <Link to="/account" aria-label="Your account">
          <Avatar name={admin?.name} />
        </Link>
      </header>

      <aside className="side" aria-label="Main">
        <Brand />
        <nav>
          {groups.map((g) => (
            <div key={g.title} className="nav-group">
              <div className="nav-title overline">{g.title}</div>
              {g.items.map(link)}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <ConnectionStatus />
          <ThemeSwitch />
          <Link to="/account" className="who" title="Your account">
            <Avatar name={admin?.name} />
            <div style={{ minWidth: 0 }}>
              <div className="who-name truncate">{admin?.name}</div>
              <div className="small faint">{admin?.role === 'OWNER' ? 'Owner' : 'Admin'}</div>
            </div>
          </Link>
        </div>
      </aside>

      <main className="main">
        <div className={`main-inner${path === '/' ? ' wide' : ''}`}>
          {path !== '/sos' && !!sos.data?.length && <SosStrip items={sos.data} />}
          <div className="page" key={path}>
            <Outlet />
          </div>
        </div>
      </main>

      <nav className="tabbar" aria-label="Tabs">
        {tabs.map((n) => (
          <Link key={n.to} to={n.to} className={`tab${n.sos ? ' sos' : ''}${isActive(path, n) ? ' active' : ''}`} aria-current={isActive(path, n) ? 'page' : undefined}>
            <n.icon />
            {n.label}
            <Count n={n} />
          </Link>
        ))}
        <button type="button" className={`tab${moreActive ? ' active' : ''}`} aria-label="More" aria-expanded={more} onClick={() => setMore(true)}>
          <IconMore />
          More
        </button>
      </nav>
      {more && <MoreSheet items={rest} path={path} onClose={() => setMore(false)} />}
    </div>
  );
}
